import { formatMoney } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { LabelFormat, LabelSubject, Symbology } from './labels.ts';
import { fitsOn, type DrawSymbol } from './symbols.ts';
import styles from './Labels.module.css';

export type StickerLook = {
  readonly format: LabelFormat;
  readonly symbology: Symbology;
  readonly showPrice: boolean;
  readonly drawSymbol: DrawSymbol;
};

const SymbolArea = ({ subject, look }: { subject: LabelSubject; look: StickerLook }) => {
  const { t } = useTranslation();
  const outcome = look.drawSymbol(look.symbology, subject.sku);
  if (outcome.kind === 'unsupported')
    return <span className={styles['symbolIssue']}>{t('labels.codeUnsupported')}</span>;
  if (!fitsOn(outcome.image, look.format, look.symbology))
    return <span className={styles['symbolIssue']}>{t('labels.codeTooLong')}</span>;
  return <img className={styles['symbol']} src={outcome.image.source} alt="" />;
};

export const Sticker = ({ subject, look }: { subject: LabelSubject | null; look: StickerLook }) => {
  if (subject === null) return <div className={styles['sticker']} aria-hidden="true" />;
  return (
    <div className={[styles['sticker'], styles[look.symbology]].join(' ')}>
      <div className={styles['stickerText']} dir="auto">
        <strong className={styles['stickerName']}>{subject.name}</strong>
        {subject.options === '' ? null : <span className={styles['stickerOptions']}>{subject.options}</span>}
        {look.showPrice ? (
          <span className={styles['stickerPrice']}>{formatMoney(subject.price, subject.locale)}</span>
        ) : null}
      </div>
      <div className={styles['stickerCode']}>
        <SymbolArea subject={subject} look={look} />
        <span className={styles['stickerSku']} dir="ltr">
          {subject.sku}
        </span>
      </div>
    </div>
  );
};
